import React, { useCallback, useEffect, useState } from 'react';
import { useQuery } from 'react-query';

import styled from 'styled-components';

import { WIDGET_TYPES } from 'common/Widgets/constants';
import { DATA_RANGE_TYPE } from 'common/Widgets/Widget/constants';
import LoaderRibbed from 'components/lib/LoaderRibbed';
import { usePublicationContext } from 'components/Publication/PublicationContext';
import { useSalesBattlecardContext } from 'components/SalesBattlecard/SalesBattlecardContext';
import { useCommunityViewContext } from 'components/View/CommunityViewContext';
import { useWidgetDataFetcher } from 'hooks/useWidgetDataFetcher';

import { PricingComparisonTable } from './PricingComparisonTable';
import CommunitySwitcher from '../ReviewsSummary/CommunitySwitcher';
import { useWidgetContext } from '../Widget/WidgetContext';

const Container = styled.div<{ isModalPreview?: boolean }>`
  display: flex;
  flex-flow: column;
  height: 100%;
  overflow: hidden;
  overflow: ${({ isModalPreview }) => (isModalPreview ? 'unset' : 'hidden')};
  width: ${({ isModalPreview }) => (isModalPreview ? '1000px' : 'unset')};
  margin-top: 25px;
  gap: 25px;
`;

export const PricingComparisonWidgetContent = () => {
  const { communityId, viewFilters } = useCommunityViewContext();
  const { selectedCompetitorId: battlecardCompetitorId } = useSalesBattlecardContext();
  const [selectedCommunityId, setSelectedCommunityId] = useState<number>(communityId);
  const { filters, isModalPreview, setMeta } = useWidgetContext();
  const { fetchWidgetData, snapshotUuids, communityContractIds } = useWidgetDataFetcher();
  const { publication } = usePublicationContext();

  const handleCommunityChange = useCallback((newCommunityId: number) => {
    setSelectedCommunityId(newCommunityId);
  }, []);

  useEffect(() => {
    if (battlecardCompetitorId !== null) {
      setSelectedCommunityId(battlecardCompetitorId);
    } else if (communityId) {
      setSelectedCommunityId(communityId);
    }
  }, [battlecardCompetitorId, viewFilters?.competitors, communityId]);

  useEffect(() => {
    setMeta((prevMeta: any) => ({
      ...(prevMeta ?? {}),
      pricingComparisonCommunityId: selectedCommunityId,
    }));
  }, [selectedCommunityId, setMeta]);

  const { data: pricingComparisonData, isFetching } = useQuery({
    queryKey: [
      'pricing-comparison',
      communityId,
      selectedCommunityId,
      filters.competitors,
      filters.careTypes,
      filters.roomTypes,
      filters.roomTypesPriceDisplay,
      filters.careLevels,
      filters.careLevelsStatsType,
      filters.comparisonTypeEnabled,
      filters.valueDisplay,
      filters.communityContractType,
      filters.communityContractSelectionType,
      filters.contractTypes,
      snapshotUuids,
      communityContractIds,
      publication?.hash,
    ],
    queryFn: () =>
      fetchWidgetData(communityId, WIDGET_TYPES.PRICING_COMPARISON, DATA_RANGE_TYPE.NOW, {
        filters: {
          competitors: selectedCommunityId !== communityId ? [selectedCommunityId] : [],
          care_types: filters.careTypes,
          room_types: filters.roomTypes,
          room_types_price_display: filters.roomTypesPriceDisplay,
          care_levels: filters.careLevels,
          care_levels_stats_type: filters.careLevelsStatsType,
          community_contract_type: filters.communityContractType,
          community_contract_selection_type: filters.communityContractSelectionType,
          contract_types: filters.contractTypes,
          review_sources: [],
          review_categories: [],
          swot_categories: [],
        },
        ...(publication?.hash ? { public_hash: publication.hash } : {}),
      }),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    keepPreviousData: true,
    enabled: communityId > 0 && snapshotUuids !== null,
  });

  return (
    <Container isModalPreview={isModalPreview} data-testid="pc-widget">
      <div data-testid="pc-widget-community-switcher">
        <CommunitySwitcher
          selectedCommunityId={selectedCommunityId}
          onCommunityChange={handleCommunityChange}
          filteredCompetitorIds={filters?.competitors}
        />
      </div>
      {isFetching ? (
        <LoaderRibbed size="small" />
      ) : (
        <PricingComparisonTable
          data={pricingComparisonData}
          comparisonTypeEnabled={filters?.comparisonTypeEnabled}
          valueDisplay={filters?.valueDisplay}
        />
      )}
    </Container>
  );
};

export default PricingComparisonWidgetContent;
